<template>
  <!-- Loading, nothing answered yet: reserve the row's height so the page never
       shifts once the dashboards arrive (Quality bar). -->
  <div
    v-if="showSkeleton"
    class="heute-grid"
    aria-hidden="true"
  >
    <div v-for="n in 4" :key="n" class="h-[104px] rounded-xl bg-surface-wash" />
  </div>

  <!-- Fewer than two tile kinds have a source: no row at all — the bands start
       directly under the date line (Outcome invariants). -->
  <div
    v-else-if="tiles.length"
    data-testid="heute-row"
    class="heute-grid"
  >
    <component
      :is="isButtonTile(tile) ? 'button' : 'div'"
      v-for="tile in tiles"
      :key="tile.kind"
      data-testid="heute-tile"
      :data-kind="tile.kind"
      :type="isButtonTile(tile) ? 'button' : undefined"
      class="flex min-w-0 flex-col items-start rounded-xl border bg-surface p-4 text-left shadow-sm"
      :class="isButtonTile(tile) ? 'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring' : ''"
      :aria-label="isButtonTile(tile) ? tileAriaLabel(tile) : undefined"
      @click="isButtonTile(tile) && handleTileClick(tile)"
    >
      <!-- Stempeluhr: never a link, its own explicit-intent control is. -->
      <!-- A person freed from working-time booking with a project timer running
           and no block open (issue 691): project time, not a clock-in. No
           clock-out here -- it would silently end her project booking; the
           project is stopped on Zeit's Start screen, which the button opens. -->
      <template v-if="tile.kind === 'clock' && tile.state === 'project_running'">
        <p class="text-xs font-semibold uppercase tracking-wider text-secondary">{{ t('homebase.heute.clock') }}</p>
        <p class="mt-1 text-2xl font-bold leading-none text-heading" data-testid="clock-project-running">{{ t('homebase.projectRunning') }}</p>
        <p v-if="tile.since" class="mt-1 text-xs text-secondary">
          {{ t('homebase.projectRunningSince', { time: clockSinceTime(tile) }) }}
        </p>
        <Button
          type="button"
          data-testid="clock-open-start"
          size="sm"
          class="mt-3"
          variant="secondary"
          @click.stop="emit('navigate', tile.moduleKey, '/dashboard')"
        >
          {{ t('homebase.open') }}
        </Button>
      </template>
      <!-- Her contract frees her from booking working time (issue 981): time-api
           refuses a clock-in, so none is offered -- the sentence and the way to a
           project. A legacy open block still falls through to the clock-out toggle. -->
      <template v-else-if="tile.kind === 'clock' && tile.state === 'clocked_out' && projectOnly(tile)">
        <p class="text-xs font-semibold uppercase tracking-wider text-secondary">{{ t('homebase.heute.clock') }}</p>
        <p class="mt-1 text-sm text-heading" data-testid="clock-work-time-free">{{ t('homebase.workTimeFree') }}</p>
        <Button
          type="button"
          data-testid="clock-open-start"
          size="sm"
          class="mt-3"
          variant="secondary"
          @click.stop="emit('navigate', tile.moduleKey, '/dashboard')"
        >
          {{ t('homebase.startProject') }}
        </Button>
      </template>
      <template v-else-if="tile.kind === 'clock'">
        <p class="text-xs font-semibold uppercase tracking-wider text-secondary">{{ t('homebase.heute.clock') }}</p>
        <p class="mt-1 max-w-full hyphens-auto break-words text-2xl font-bold leading-none text-heading tabular-nums">
          {{ clockState(tile) === 'clocked_in' ? clockDuration(tile) : t('homebase.clockedOut') }}
        </p>
        <p v-if="clockState(tile) === 'clocked_in'" class="mt-1 text-xs text-secondary">
          {{ t('homebase.clockedInSince', { time: clockSinceTime(tile) }) }}
        </p>
        <!-- Clocked in: what the open working time is booked on, Zeit's "Woran
             arbeitest du?" -- the field opens the project and Tätigkeit sheet.
             Absent without a bookable project, as in Zeit. -->
        <button
          v-if="clockState(tile) === 'clocked_in' && clockProject"
          type="button"
          class="heute-project mt-3"
          :style="{ '--row-color': clockProject.color ?? undefined }"
          :aria-label="t('homebase.clockProject.fieldLabel', { current: clockProject.projectName ?? t('homebase.clockProject.none') })"
          aria-haspopup="dialog"
          :disabled="clockBusy"
          data-testid="clock-project-field"
          @click.stop="emit('chooseProject')"
        >
          <span :class="clockProject.projectName ? 'heute-project__dot' : 'heute-project__swatch'" aria-hidden="true" />
          <span class="heute-project__main">
            <span class="heute-project__name" data-testid="clock-project-name">{{ clockProject.projectName ?? t('homebase.clockProject.none') }}</span>
            <span class="heute-project__sub">{{ clockProject.projectName ? clockProject.detail : t('homebase.clockProject.choose') }}</span>
          </span>
          <LumiIcon name="next" class="heute-project__chevron" aria-hidden="true" />
        </button>
        <Button
          type="button"
          data-testid="clock-toggle"
          size="sm"
          class="heute-clock-action mt-3"
          :variant="clockState(tile) === 'clocked_in' ? 'secondary' : 'primary'"
          :disabled="clockBusy"
          @click.stop="emit('clock', clockModuleKey(tile), clockState(tile) === 'clocked_in' ? 'clock_out' : 'clock_in')"
        >
          <template #icon><LumiIcon :name="clockState(tile) === 'clocked_in' ? 'clock-out' : 'clock-in'" /></template>
          {{ clockState(tile) === 'clocked_in' ? t('homebase.clockOut') : t('homebase.clockIn') }}
        </Button>
      </template>

      <!-- The next date that concerns me — the whole tile is the control. -->
      <template v-else-if="tile.kind === 'date'">
        <span class="text-xs font-semibold uppercase tracking-wider text-secondary">{{ t(`homebase.heute.kind.${dateKind(tile)}`) }}</span>
        <span class="mt-1 text-2xl font-bold leading-none text-heading tabular-nums">{{ dateBig(tile) }}</span>
        <span class="mt-1 w-full truncate text-xs text-secondary">{{ dateLabel(tile) }}</span>
      </template>

      <!-- "Braucht mich" — never a link itself, its chips are. -->
      <template v-else-if="tile.kind === 'attention'">
        <span class="text-xs font-semibold uppercase tracking-wider text-secondary">{{ t('homebase.heute.attention') }}</span>
        <span class="mt-1 text-2xl font-bold leading-none text-heading tabular-nums">{{ attentionTotal(tile) }}</span>
        <div class="mt-2 flex flex-wrap gap-1.5">
          <button
            v-for="chip in attentionChips(tile)"
            :key="chip.moduleKey + ':' + chip.factKey"
            type="button"
            data-testid="heute-chip"
            class="rounded-full bg-surface-wash px-2.5 py-1 text-xs text-body focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
            :disabled="!chip.deepLink"
            @click.stop="chip.deepLink && emit('navigate', chip.moduleKey, chip.deepLink)"
          >
            {{ chipText(chip) }}
          </button>
        </div>
      </template>

      <!-- "Neu" — unread news lines, whole tile opens the source module's root. -->
      <template v-else-if="tile.kind === 'news'">
        <span class="text-xs font-semibold uppercase tracking-wider text-secondary">{{ t('homebase.heute.news') }}</span>
        <span class="mt-1 text-2xl font-bold leading-none text-heading tabular-nums">{{ newsBig(tile) }}</span>
        <span class="mt-1 w-full truncate text-xs text-secondary">{{ newsModuleNames(tile) }}</span>
      </template>

      <!-- "Urlaub übrig" — whole tile a control only when the fact carries a deep link. -->
      <template v-else-if="tile.kind === 'leave'">
        <span class="text-xs font-semibold uppercase tracking-wider text-secondary">{{ t('homebase.heute.leaveRemaining') }}</span>
        <span class="mt-1 text-2xl font-bold leading-none text-heading tabular-nums">{{ leaveBig(tile) }}</span>
      </template>
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, LUMI_MODULES, type LumiModuleKey, LumiIcon } from '@lumi/ui';
import {
  composeHeuteRow,
  formatUpcomingBigText,
  type HeuteTile,
  type HeuteAttentionChip,
} from '@/utils/heute';
import { localizedLabel, type ModuleDashboard } from '@/utils/homebase';
import { USER_API_TO_LUMI } from '@/utils/moduleSwitcher';

const props = defineProps<{
  dashboards: Record<string, ModuleDashboard | null>;
  order: string[];
  loading: boolean;
  clockBusy: boolean;
  /** Clocked in with a bookable project: what runs now (useClockProjects). Null = no choice offered. */
  clockProject?: { projectName: string | null; color: string | null; detail: string } | null;
}>();

const emit = defineEmits<{
  clock: [moduleKey: string, intent: 'clock_in' | 'clock_out'];
  navigate: [moduleKey: string, deepLink: string | undefined];
  chooseProject: [];
}>();

const { t, te, locale } = useI18n();

// No answer at all yet (first paint / tenant switch): reserve the row's height
// rather than flashing a one-tile or empty row while the fan-out is in flight.
const showSkeleton = computed(() => props.loading && Object.keys(props.dashboards).length === 0);
const tiles = computed<HeuteTile[]>(() => composeHeuteRow(props.dashboards, props.order));

// A 60s ticker for the running clock duration — coarse on purpose, the same
// granularity the big text renders in ("h:mm h").
const tickNow = ref(Date.now());
let ticker: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
  ticker = setInterval(() => {
    tickNow.value = Date.now();
  }, 60_000);
});
onUnmounted(() => {
  if (ticker) clearInterval(ticker);
});

function isButtonTile(tile: HeuteTile): boolean {
  if (tile.kind === 'date' || tile.kind === 'news') return true;
  if (tile.kind === 'leave') return !!tile.deepLink;
  return false;
}

function handleTileClick(tile: HeuteTile) {
  if (tile.kind === 'date') emit('navigate', tile.moduleKey, tile.upcoming.deepLink);
  else if (tile.kind === 'news') emit('navigate', tile.moduleKeys[0], undefined);
  else if (tile.kind === 'leave' && tile.deepLink) emit('navigate', tile.moduleKey, tile.deepLink);
}

function tileAriaLabel(tile: HeuteTile): string {
  if (tile.kind === 'date') return `${t(`homebase.heute.kind.${tile.upcoming.kind}`)} ${dateBig(tile)}`;
  if (tile.kind === 'news') return `${t('homebase.heute.news')} ${newsBig(tile)}`;
  if (tile.kind === 'leave') return `${t('homebase.heute.leaveRemaining')} ${leaveBig(tile)}`;
  return '';
}

// ── Clock ────────────────────────────────────────────────────────────────
function clockState(tile: HeuteTile): 'clocked_in' | 'clocked_out' | 'project_running' | null {
  return tile.kind === 'clock' ? tile.state : null;
}
// The server's word that her contract frees her from booking working time.
function projectOnly(tile: HeuteTile): boolean {
  return tile.kind === 'clock' && props.dashboards[tile.moduleKey]?.weekProgress?.projectOnly === true;
}
function clockModuleKey(tile: HeuteTile): string {
  return tile.kind === 'clock' ? tile.moduleKey : '';
}
function clockDuration(tile: HeuteTile): string {
  if (tile.kind !== 'clock' || tile.state !== 'clocked_in' || !tile.since) return '';
  const since = new Date(tile.since).getTime();
  if (Number.isNaN(since)) return '';
  const minutes = Math.max(0, Math.floor((tickNow.value - since) / 60_000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')} h`;
}
function clockSinceTime(tile: HeuteTile): string {
  if (tile.kind !== 'clock' || !tile.since) return '';
  const d = new Date(tile.since);
  return Number.isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat(locale.value, { hour: '2-digit', minute: '2-digit' }).format(d);
}

// ── Date ─────────────────────────────────────────────────────────────────
function dateKind(tile: HeuteTile): string {
  return tile.kind === 'date' ? tile.upcoming.kind : '';
}
function dateBig(tile: HeuteTile): string {
  return tile.kind === 'date' ? formatUpcomingBigText(tile.upcoming, new Date(), locale.value) : '';
}
function dateLabel(tile: HeuteTile): string {
  return tile.kind === 'date' ? tile.upcoming.label : '';
}

// ── Attention ────────────────────────────────────────────────────────────
function attentionTotal(tile: HeuteTile): number {
  return tile.kind === 'attention' ? tile.total : 0;
}
function attentionChips(tile: HeuteTile): HeuteAttentionChip[] {
  return tile.kind === 'attention' ? tile.chips : [];
}
function chipText(chip: HeuteAttentionChip): string {
  const label = localizedLabel({ key: chip.factKey, label: chip.label }, t, te, chip.dashboardModule);
  return `${chip.value} ${label}`;
}

// ── News ─────────────────────────────────────────────────────────────────
function newsBig(tile: HeuteTile): string {
  if (tile.kind !== 'news') return '';
  return tile.capped ? `${tile.total}+` : String(tile.total);
}
function moduleName(moduleKey: string): string {
  const lumi = (USER_API_TO_LUMI[moduleKey] ?? moduleKey) as LumiModuleKey;
  return lumi === 'admin' || !LUMI_MODULES[lumi] ? moduleKey : t(`lumiModules.${lumi}.name`);
}
// No preposition, no grammar to translate (workspace-start M2 F6b) — the
// sub-line is just the source module name(s), " · "-joined like the rest of
// the app's inline lists.
function newsModuleNames(tile: HeuteTile): string {
  return tile.kind === 'news' ? tile.moduleKeys.map(moduleName).join(' · ') : '';
}

// ── Leave ────────────────────────────────────────────────────────────────
function leaveBig(tile: HeuteTile): string {
  if (tile.kind !== 'leave') return '';
  const n = typeof tile.value === 'number' ? tile.value : Number(tile.value);
  const formatted = Number.isFinite(n) ? new Intl.NumberFormat(locale.value, { maximumFractionDigits: 1 }).format(n) : String(tile.value);
  const unit = t('homebase.heute.days', Number.isFinite(n) ? n : 1);
  return `${formatted} ${unit}`;
}
</script>

<style scoped>
/* Up to four tiles, as many as the row's own width holds (issue 1026): the
   columns follow the space the shell leaves the page — a folded or open sidebar,
   a docked Lumi — not the viewport, so a tile never gets too narrow for
   "Ausgestempelt" and the row never runs out of its panel. The track floor is the
   larger of a readable tile and a quarter of the row, which caps it at four. */
.heute-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, max(13.5rem, calc((100% - 2.25rem) / 4))), 1fr));
}

/* The project field: a full-width picker row, tall enough to hit with a thumb. */
.heute-project {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  min-height: 48px;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-wash);
  font: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.heute-project:hover:not(:disabled),
.heute-project:active:not(:disabled) {
  background: var(--selected-bg);
}
.heute-project:disabled {
  cursor: progress;
  opacity: 0.6;
}
.heute-project:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.heute-project__dot,
.heute-project__swatch {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}
.heute-project__dot {
  border-radius: var(--radius-pill);
  background: var(--row-color);
}
.heute-project__swatch {
  border-radius: 4px;
  background: repeating-linear-gradient(135deg, transparent 0 2px, color-mix(in srgb, var(--text-tertiary) 45%, transparent) 2px 3.5px);
  box-shadow: inset 0 0 0 1px var(--border-strong);
}
.heute-project__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.heute-project__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-heading);
}
.heute-project__sub {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-xs);
  color: var(--text-secondary);
}
.heute-project__chevron {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--text-tertiary);
}

/* On a phone the row is one column, so the clock button spans the tile at the
   large control height: one confident thumb tap to clock in or out. */
@media (max-width: 767px) {
  .heute-clock-action {
    width: 100%;
    height: var(--control-h-lg);
    font-size: var(--text-base);
  }
  .heute-project {
    min-height: 56px;
  }
  .heute-project__name {
    font-size: var(--text-base);
  }
  .heute-project__sub {
    font-size: var(--text-label);
  }
}
</style>
